<script>
  import { auth, isOnline } from '../stores.js';
  import { authenticateWithCode } from '../lib/sync.js';
  import { push } from 'svelte-spa-router';

  let code = '';
  let rememberMe = false;
  let loading = false;
  let error = '';

  async function handleLogin() {
    if (!code.trim()) {
      error = 'Veuillez entrer un code';
      return;
    }

    if (!$isOnline) {
      error = 'Vous devez être en ligne pour vous connecter';
      return;
    }

    loading = true;
    error = '';

    try {
      const result = await authenticateWithCode(code.trim(), rememberMe);

      if (result.success) {
        // Auth is already saved by authenticateWithCode
        // Just need to initialize the auth store
        await auth.init();

        // Redirect to home
        push('/');
      } else {
        error = result.error || 'Code invalide ou expiré';
      }
    } catch (err) {
      error = 'Une erreur est survenue. Veuillez réessayer.';
      console.error('Login error:', err);
    } finally {
      loading = false;
    }
  }

  function handleKeyPress(event) {
    if (event.key === 'Enter') {
      handleLogin();
    }
  }
</script>

<div class="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-base-200 to-base-300">
  <div class="card w-full max-w-md bg-base-100 shadow-2xl">
    <div class="card-body">
      <div class="flex flex-col items-center mb-6">
        <svg class="w-20 h-20 mb-4 rounded-2xl" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
          <rect width="512" height="512" fill="#374151" rx="64"/>
          <g transform="translate(128, 128)">
            <rect x="40" y="20" width="200" height="240" fill="#fff" rx="8"/>
            <rect x="50" y="30" width="180" height="220" fill="#f8f9fa" rx="4"/>
            <line x1="70" y1="60" x2="210" y2="60" stroke="#374151" stroke-width="4" stroke-linecap="round"/>
            <line x1="70" y1="85" x2="210" y2="85" stroke="#e0e7ff" stroke-width="4" stroke-linecap="round"/>
            <line x1="70" y1="110" x2="180" y2="110" stroke="#e0e7ff" stroke-width="4" stroke-linecap="round"/>
            <g transform="translate(100, 140)">
              <path d="M 40 0 Q 40 -15, 30 -25 Q 20 -35, 0 -35 Q -20 -35, -30 -25 Q -40 -15, -40 0 L -20 0 Q -20 -10, -15 -15 Q -10 -20, 0 -20 Q 10 -20, 15 -15 Q 20 -10, 20 0 Q 20 10, 10 20 L 0 30 L 0 45"
                    fill="none" stroke="#374151" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="0" cy="60" r="6" fill="#374151"/>
            </g>
            <path d="M 40 20 L 50 10 L 50 270 L 40 260 Z" fill="#1f2937"/>
          </g>
        </svg>
        <h1 class="text-3xl font-bold text-base-content">QuizClasse</h1>
        <p class="text-base-content mt-1">Application éducative</p>
      </div>

      <div class="form-control">
        <label class="label" for="code">
          <span class="label-text font-bold text-base-content">Code de connexion</span>
        </label>
        <input
          id="code"
          type="text"
          bind:value={code}
          on:keypress={handleKeyPress}
          placeholder="Entrez votre code"
          disabled={loading}
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          class="input input-bordered w-full border-2 border-base-content focus:border-primary"
        />
      </div>

      <div class="form-control mt-4">
        <label class="label cursor-pointer justify-start gap-3">
          <input
            type="checkbox"
            bind:checked={rememberMe}
            disabled={loading}
            class="checkbox checkbox-primary border-2 border-base-content"
          />
          <span class="text-base font-semibold text-base-content">Se souvenir de moi</span>
        </label>
      </div>

      {#if error}
        <div class="alert alert-error mt-4" role="alert">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      {/if}

      {#if !$isOnline}
        <div class="alert alert-warning mt-4" role="alert">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Vous êtes hors ligne. La connexion nécessite une connexion Internet.</span>
        </div>
      {/if}

      <button
        class="btn btn-primary w-full mt-6"
        on:click={handleLogin}
        disabled={loading || !$isOnline}
      >
        {#if loading}
          <span class="loading loading-spinner loading-sm"></span>
          Connexion en cours...
        {:else}
          Se connecter
        {/if}
      </button>

      <p class="text-center text-sm text-base-content/80 mt-6">
        Une fois connecté, vous pourrez apprendre même hors ligne
      </p>
    </div>
  </div>
</div>
